import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wrench, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { EstimateFileField } from "@/components/EstimateFileField";

const inputCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 h-9";
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

// Flag a unit "As-Is" and log what it needs to pass state inspection (line items + costs).
export default function AsIsDialog({ vehicle, open, onOpenChange, onSaved }) {
  const [asIs, setAsIs] = useState(false);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [showEstimate, setShowEstimate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(vehicle);   // tracks the document attached while the dialog is open

  useEffect(() => {
    if (!vehicle) return;
    setCurrent(vehicle);
    setAsIs(!!vehicle.as_is);
    setItems(vehicle.repair_items?.length ? vehicle.repair_items.map((i) => ({ item: i.item, cost: i.cost ?? "" })) : [{ item: "", cost: "" }]);
    setNotes(vehicle.as_is_notes || "");
    setShowEstimate(!!vehicle.as_is_show_estimate);
  }, [vehicle, open]);

  const total = items.reduce((s, i) => s + (Number(i.cost) || 0), 0);
  const update = (idx, patch) => setItems((list) => list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => setItems((list) => (list.length > 1 ? list.filter((_, i) => i !== idx) : [{ item: "", cost: "" }]));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/vehicles/${vehicle.id}/as-is`, {
        as_is: asIs,
        as_is_notes: notes,
        repair_items: items.filter((i) => i.item.trim()).map((i) => ({ item: i.item.trim(), cost: Number(i.cost) || 0 })),
        as_is_show_estimate: showEstimate,
      });
      onSaved?.(data);
      toast.success(asIs ? `Flagged As-Is · estimate ${money(data.repair_estimate_total)}` : "As-Is flag removed");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!vehicle) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="as-is-dialog" className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-400" /> As-Is &amp; inspection estimate
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {vehicle.year} {vehicle.make} {vehicle.model} · Stock {vehicle.stock_number || "—"} · VIN {vehicle.vin}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
            <div>
              <div className="text-sm font-medium">Sell this unit As-Is</div>
              <div className="text-[11px] text-slate-500">Needs work to pass state inspection — flagged on the card and noted in every CRM lead.</div>
            </div>
            <Switch data-testid="as-is-switch" checked={asIs} onCheckedChange={setAsIs} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-300">What it needs to pass inspection <span className="text-slate-500 font-normal">(optional if you upload the shop estimate)</span></Label>
              <span className="font-mono-tech text-xs text-slate-400" data-testid="as-is-total">Estimate {money(total)}</span>
            </div>
            <div className="space-y-2" data-testid="repair-items">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2" data-testid={`repair-item-${idx}`}>
                  <Input data-testid={`repair-item-name-${idx}`} value={it.item} onChange={(e) => update(idx, { item: e.target.value })} placeholder="e.g. Front brake pads & rotors" className={`${inputCls} flex-1`} />
                  <div className="relative w-32">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input data-testid={`repair-item-cost-${idx}`} type="number" min="0" step="10" value={it.cost} onChange={(e) => update(idx, { cost: e.target.value })} placeholder="0" className={`${inputCls} pl-6`} />
                  </div>
                  <button type="button" onClick={() => remove(idx)} data-testid={`repair-item-remove-${idx}`} title="Remove line" className="h-9 w-9 rounded-md border border-slate-800 text-slate-500 hover:text-red-400 hover:border-red-900 flex items-center justify-center">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setItems((l) => [...l, { item: "", cost: "" }])} data-testid="repair-item-add" className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#7FC3FF] hover:text-white">
              <Plus className="h-3.5 w-3.5" /> Add line
            </button>
          </div>

          <div>
            <Label className="text-slate-300 mb-1.5 block">Notes (optional · internal)</Label>
            <Textarea data-testid="as-is-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Inspection sticker expired 3/26, check-engine light on, shop quote attached in DMS…" className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 min-h-[70px]" />
          </div>

          <EstimateFileField vehicle={current} disabled={!asIs} onChanged={(u) => { setCurrent(u); onSaved?.(u); }} />

          <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              {showEstimate ? <Eye className="h-4 w-4 text-[#7FC3FF]" /> : <EyeOff className="h-4 w-4 text-slate-500" />}
              <div>
                <div className="font-medium">Show the estimate to shoppers</div>
                <div className="text-[11px] text-slate-500">Shoppers tap the As-Is badge on the QR page to open your document — or the line items if there's no document. Off = badge only.</div>
              </div>
            </div>
            <Switch data-testid="as-is-show-estimate-switch" checked={showEstimate} onCheckedChange={setShowEstimate} disabled={!asIs} />
          </div>

          <Button onClick={save} disabled={busy} data-testid="as-is-save-btn" className="w-full h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
